/**
 * User Reducer Helper Function
 */

import { environment } from '../../relay';
import { LoginFormMutation } from '../../relay/login';
import { LogoutMutation } from '../../relay/logout';
import { UpdateProfileMutation } from '../../relay/profile';

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  ISignUpResult,
} from 'amazon-cognito-identity-js';

import { commitMutation } from 'react-relay';
import { PayloadError } from 'relay-runtime';

export const logInUser = ({ email, password }: IUserCredentials, userPool: CognitoUserPool) =>
  new Promise<CognitoUserSession>((resolve, reject) => {
    const authenticationData = {
      Username: email,
      Password: password,
    };

    const userData = {
      Username: email,
      Pool: userPool,
    };

    const authenticationDetails = new AuthenticationDetails(authenticationData);
    const cognitoUser = new CognitoUser(userData);

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: resolve,
      onFailure: reject,
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // User was signed up by an admin and must provide new
        // password and required attributes, if any, to complete
        // authentication.

        // The api doesn't accept this field back
        delete userAttributes.email_verified;
        const newPassword = prompt('Enter your new password') || '';
        // Get new password. This same workflow applies for password reset.
        cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
          onSuccess: (cognitoSession) => {
            resolve(cognitoSession);
          },
          onFailure: (err: any) => {
            reject(err);
          },
        });
      },
    });
  });

export const signUpUser = ({ email, password }: IUserCredentials, userPool: CognitoUserPool) =>
  new Promise<CognitoUser>((resolve, reject) => {
    const attributeData = [
      { Name: 'given_name', Value: '' },
      { Name: 'family_name', Value: '' },
      { Name: 'email', Value: email },
    ];
    const userAttributes = attributeData.map((data) => new CognitoUserAttribute(data));

    userPool.signUp(email, password, userAttributes, [], (err: Error, result: ISignUpResult) => {
      err ? reject(err) : resolve(result!.user);
    });
  });

export const resetPassword = ({ email, code, password }: IResetPasswordActionPayload, userPool: CognitoUserPool) =>
  new Promise<void>((resolve, reject) => {
    const userData = {
      Username: email,
      Pool: userPool,
    };

    const cognitoUser = new CognitoUser(userData);

    if (code && password) {
      cognitoUser.confirmPassword(code, password, {
        onSuccess: resolve,
        onFailure: reject,
      });
    } else {
      cognitoUser.forgotPassword({
        onSuccess: resolve,
        onFailure: reject,
      });
    }
  });

export const authenticateWithToken = ({ idToken, viewerId }: IUserTokenAuthenticationActionPayload) =>
  new Promise<PayloadError[] | null>((resolve, reject) => {
    commitMutation(environment, {
      mutation: LoginFormMutation,
      updater: (storeProxy) => {
        const viewerRecord = storeProxy.get(viewerId)!;
        const loginRecord = storeProxy.getRootField('login');
        if (loginRecord) {
          const newProfileRecord = loginRecord.getLinkedRecord('profile')!;
          const cartRecord = loginRecord.getLinkedRecord('cart')!;
          viewerRecord.setLinkedRecord(newProfileRecord, 'profile');
          viewerRecord.setLinkedRecord(cartRecord, 'cart');
        }
      },
      variables: { input: { id_token: idToken } },
      onCompleted: (_, errors) => resolve(errors),
      onError: reject,
    });
  });

export const updateProfile = ({ input, viewerId }: IUpdateUserProfileActionPayload) =>
  new Promise<PayloadError[] | null | IMutationResult>((resolve, reject) => {
    const variables = {
      profileInput: input,
    };

    commitMutation(environment, {
      mutation: UpdateProfileMutation,
      updater: (store) => {
        const viewerRecord = store.get(viewerId)!;
        const updateProfileRecord = store.getRootField('updateProfile');
        if (updateProfileRecord) {
          const newProfileRecord = updateProfileRecord.getLinkedRecord('profile')!;
          viewerRecord.setLinkedRecord(newProfileRecord, 'profile');
        }
      },
      variables,
      onCompleted: (response, errors) => resolve(response),
      onError: reject,
    });
  });

export const logOutUser = ({ viewerId }) =>
  new Promise((resolve, reject) => {
    commitMutation(environment, {
      mutation: LogoutMutation,
      updater: (storeProxy, errors) => {
        const viewerRecord = storeProxy.get(viewerId)!;
        const profileRecord = viewerRecord.getLinkedRecord('profile');
        if (profileRecord) {
          const id = profileRecord.getDataID();
          storeProxy.delete(id);
        }

        // IMPORTANT: the cart needs to be unset to prevent the anonymous user
        // from modifying a logged in user's cart.
        viewerRecord.setValue(null, 'cart');
      },
      variables: { input: {} },
      onCompleted: (response, errors) => resolve({ response, errors }),
      onError: reject,
    });
  });

export const sendConfirmationEmail = (_, userPool: CognitoUserPool) =>
  new Promise<any>((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (cognitoUser) {
      cognitoUser.getSession((error, session) => {
        if (error) {
          reject(error);
          return;
        }

        cognitoUser.getAttributeVerificationCode('email', {
          onSuccess: resolve,
          onFailure: (failureError) => reject(failureError),
          inputVerificationCode: resolve,
        });
      });
    } else {
      reject(new Error('Cognito user not found.'));
    }
  });

export const verifyUserEmail = ({ email, code }: IVerifyEmailActionPayload, userPool: CognitoUserPool) =>
  new Promise<any>((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (cognitoUser) {
      // verifyAttribute requires a valid access token, so we need to
      // call getSession to make sure we get a valid access token if
      // the old one has expired

      // If the user accesses the verification link in a browser where
      // they are not logged in, we need to have them log in and then
      // auto-redirect back to the verification page with the code/email.
      cognitoUser.getSession((error, session) => {
        if (error) {
          reject(error);
          return;
        }

        cognitoUser.verifyAttribute('email', code, {
          onSuccess: resolve,
          onFailure: (failureError) => reject(failureError),
        });
      });
    } else {
      reject({
        name: 'CognitoUserNotFound',
        message: 'User must be logged in in order to verify his email.',
      });
    }
  });



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/redux/helpers/user.ts